/**
 * RecordPaymentModal — register a partial or full installment payment
 * against a Finance invoice. Posts to POST /api/finance/payments which
 * automatically updates `amount_paid`, `balance_due`, transitions the
 * invoice to "paid" when the balance reaches zero, and (via the pipeline
 * bridge) advances the linked opportunity / deal.
 */
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { X, Loader2, Wallet } from 'lucide-react';
import { Button } from '../../ui/button';
import { useToast } from '../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHODS = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'other', label: 'Other' },
];

const RecordPaymentModal = ({ isOpen, invoice, onClose, onRecorded }) => {
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');

  const outstanding = useMemo(() => {
    if (!invoice) return 0;
    const t = Number(invoice.total) || 0;
    const ap = Number(invoice.amount_paid) || 0;
    const bd = invoice.balance_due != null ? Number(invoice.balance_due) : (t - ap);
    return Math.max(0, Math.round(bd * 100) / 100);
  }, [invoice]);

  const [amount, setAmount] = useState(outstanding);
  const [method, setMethod] = useState('bank_transfer');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Refresh defaults when modal re-opens with a different invoice.
  React.useEffect(() => {
    if (isOpen) {
      setAmount(outstanding);
      setMethod('bank_transfer');
      setDate(new Date().toISOString().slice(0, 10));
      setReference('');
      setNotes('');
    }
  }, [isOpen, outstanding]);

  if (!isOpen || !invoice) return null;

  const amt = Number(amount) || 0;
  const remainingAfter = Math.max(0, Math.round((outstanding - amt) * 100) / 100);
  const willClose = amt > 0 && remainingAfter <= 0.01;
  const tooMuch = amt - outstanding > 0.01;

  const fmt = (n) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: invoice.currency || 'AED',
      maximumFractionDigits: 2,
    }).format(Number(n) || 0);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (amt <= 0) {
      toast({ variant: 'destructive', title: 'Invalid amount', description: 'Amount must be greater than zero' });
      return;
    }
    if (tooMuch) {
      toast({
        variant: 'destructive',
        title: 'Amount exceeds balance',
        description: `Outstanding balance is ${fmt(outstanding)}.`,
      });
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/finance/payments`,
        {
          invoice_id: invoice.id,
          amount: amt,
          payment_method: method,
          payment_date: date,
          reference: reference || null,
          notes_en: notes || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({
        title: willClose ? 'Invoice paid in full' : 'Installment recorded',
        description: willClose
          ? `${fmt(amt)} received. Invoice marked as PAID.`
          : `${fmt(amt)} received. Remaining balance: ${fmt(remainingAfter)}.`,
      });
      onRecorded?.();
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Failed to record payment';
      toast({ variant: 'destructive', title: 'Error', description: String(detail) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      data-testid="record-payment-modal"
    >
      <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b bg-gradient-to-r from-[#023E8A] to-[#0353A4] text-white rounded-t-xl">
          <div className="flex items-center gap-2">
            <Wallet className="w-5 h-5" />
            <h3 className="font-bold">Record Payment</h3>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="record-payment-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3 bg-gray-50 border rounded-lg p-3 text-sm">
            <div>
              <div className="text-[11px] uppercase text-gray-500">Invoice Total</div>
              <div className="font-semibold text-[#2B2D42]">{fmt(invoice.total)}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-gray-500">Paid so far</div>
              <div className="font-semibold text-green-700">{fmt(invoice.amount_paid)}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-gray-500">Outstanding</div>
              <div className="font-bold text-[#FF6B35]" data-testid="record-payment-outstanding">
                {fmt(outstanding)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Amount ({invoice.currency || 'AED'}) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-base font-mono"
                data-testid="record-payment-amount"
                required
              />
              <div className="flex flex-wrap gap-2 mt-2">
                {[0.25, 0.5, 0.75, 1].map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setAmount(Math.round(outstanding * f * 100) / 100)}
                    className="text-[11px] px-2.5 py-1 border border-gray-300 hover:border-[#00B4D8] hover:bg-[#00B4D8]/10 hover:text-[#00B4D8] text-gray-700 rounded-full transition"
                    data-testid={`record-payment-preset-${Math.round(f * 100)}`}
                  >
                    {f === 1 ? 'Full' : `${Math.round(f * 100)}%`}
                  </button>
                ))}
              </div>
              {tooMuch && (
                <p className="text-[11px] text-red-600 mt-1">
                  Amount exceeds outstanding balance of {fmt(outstanding)}.
                </p>
              )}
              {!tooMuch && amt > 0 && !willClose && (
                <p className="text-[11px] text-gray-600 mt-1">
                  Remaining balance after this installment: <strong>{fmt(remainingAfter)}</strong>
                </p>
              )}
              {willClose && (
                <p className="text-[11px] text-green-700 mt-1 font-semibold">
                  This payment closes the invoice — status will switch to PAID and the linked opportunity / deal will advance.
                </p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Method *</label>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg bg-white text-sm"
                data-testid="record-payment-method"
              >
                {PAYMENT_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Payment Date *</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                data-testid="record-payment-date"
                required
              />
            </div>

            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Reference (bank ref / check #)</label>
              <input
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="TRF-2026-…"
                className="w-full px-3 py-2 border rounded-lg text-sm"
                data-testid="record-payment-reference"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
              <textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Internal note for this installment…"
                className="w-full px-3 py-2 border rounded-lg text-sm"
                data-testid="record-payment-notes"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button type="button" variant="outline" onClick={onClose} data-testid="record-payment-cancel">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || amt <= 0 || tooMuch}
              className="bg-[#00B4D8] hover:bg-[#0096c7]"
              data-testid="record-payment-submit"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wallet className="w-4 h-4 mr-2" />}
              {willClose ? 'Record final payment' : 'Record installment'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecordPaymentModal;
